import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Loader from "../../components/Loader";
import SEO from "../../components/seo";
import LayoutOne from "../../layouts/LayoutOne";
import Breadcrumb from "../../wrappers/breadcrumb/Breadcrumb";
import { Container, Row, Col, Card, Accordion, Modal, Button } from "react-bootstrap";

import Table from "../../components/Table";

import {
  authSelector,
  getReturnDetails,
  updateReturnHeader,
} from "../../store/slices/auth-slice";

import { createColumnHelper } from "@tanstack/react-table";
import { formatDate, getCurrencySymbol } from "../../helpers/common";
import { ensureOrderClient, startSubstituteOrderCart } from "../../helpers/substituteOrder";

const ReturnDetails = () => {
  const dispatch = useDispatch();
  const {t} = useTranslation();
  const { orderId, returnId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const { isLoading, client, cartProductCount } = useSelector(authSelector);

  const [returnDetails, setReturnDetails] = useState(null);
  const [showResetModal, setShowResetModal] = useState(false);

  useEffect(() => {
    if (!returnId) return;
    fetchReturnDetails(returnId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returnId, orderId]);

  const fetchReturnDetails = async (returnId) => {
    try {
      dispatch(
        getReturnDetails({
          returnId,
          setReturnDetails: (data) => {
            setReturnDetails(data);
          },
        })
      );
    } catch (error) {
      console.error("Error fetching return details:", error);
    }
  };

  const columnHelper = createColumnHelper();
  const returnItemsColumns = useMemo(
    () => [
      // Same product columns as the create return page; "-" when the item has no parent model
      columnHelper.accessor("parentInternalName", {
        header: t("Model"),
        cell: (info) => info.getValue() || "-",
      }),
      columnHelper.accessor("description", {
        header: t("Name"),
        cell: (info) => info.getValue() || "",
      }),
      columnHelper.accessor("color", {
        header: t("Color"),
        cell: (info) => info.getValue() || "",
      }),
      columnHelper.accessor("size", {
        header: t("Size"),
        cell: (info) => info.getValue() || "",
      }),
      columnHelper.accessor("returnReasonDesc", {
        header: t("Reason"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("returnTypeDesc", {
        header: t("Type"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("returnItemStatus", {
        header: t("Status"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("returnQuantity", {
        header: t("Return Qty"),
        cell: (info) => info.getValue(),
      }),
      columnHelper.accessor("returnPrice", {
        header: t("Return Price"),
        cell: (item) =>
          `${getCurrencySymbol(
            returnDetails?.currencyUomId
          )}${item.getValue()}`,
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnHelper]
  );

  const substituteOrdersColumns = useMemo(
    () => [
      columnHelper.accessor("orderId", {
        header: t("Order ID"),
        cell: ({ row, getValue }) => {
          const orderId = getValue() || "";
          return <Link to={`/orders/${orderId}`}>{orderId}</Link>;
        },
      }),
      columnHelper.accessor("grandTotal", {
        header: t("Grand Total"),
        cell: (item) =>
          `${getCurrencySymbol(
            returnDetails?.currencyUomId
          )}${item.getValue()}`,
      }),
      columnHelper.accessor("statusDes", {
        header: t("Status"),
        // cell: (info) => info.getValue(),
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [columnHelper]
  );

  // Stored return classification from getReturnDetail (Y = commercial substitution)
  const isSubstitutionReturn = returnDetails?.isSubstitution === "Y";

  // A substitute order can be (re)started from here when the order step was skipped or its order was cancelled:
  // not on a cancelled or completed return, and only while no other substitute order is active (same rules as the backend)
  const canCreateSubstituteOrder =
    isSubstitutionReturn &&
    !["RETURN_CANCELLED", "RETURN_COMPLETED"].includes(returnDetails?.statusId) &&
    !(returnDetails?.substituteOrders || []).some((order) => order.statusId !== "ORDER_CANCELLED");

  const createSubstituteOrder = () => {
    setShowResetModal(false);
    // The catalog needs the return's customer as client (fromPartyId = the original order's bill-to customer)
    ensureOrderClient({
      dispatch,
      client,
      partyId: returnDetails?.fromPartyId,
      t,
      onReady: () => startSubstituteOrderCart({ dispatch, navigate, returnId, orderId }),
    });
  };

  // The substitute cart replaces the current one: ask first when it has items
  const handleCreateSubstituteOrder = () => {
    if (cartProductCount > 0) {
      setShowResetModal(true);
      return;
    }
    createSubstituteOrder();
  };

  // Amount in the return currency, 2 decimals; "N/A" only when the value is missing
  const formatReturnAmount = (value) =>
    value === null || value === undefined || value === ""
      ? t("N/A")
      : `${getCurrencySymbol(returnDetails?.currencyUomId)}${Number(value).toFixed(2)}`;

  const handleStatusChange = () => {
    if (!returnDetails) return;

    const payload = {
      fromPartyId: returnDetails.fromPartyId,
      returnId: returnDetails.returnId,
      entryDate: formatDate(returnDetails?.entryDate),
      originContactMechId: "",
      billingAccountId: "",
      returnHeaderTypeId: "CUSTOMER_RETURN",
      finAccountId: "",
      currencyUomId: returnDetails.currencyUomId,
      statusId: "SUP_RETURN_ACCEPTED",
      createdBy: returnDetails.createdBy,
      paymentMethodId: "",
      needsInventoryReceive: returnDetails.needsInventoryReceive || "",
      toPartyId: returnDetails.toPartyId || "",
      destinationFacilityId: returnDetails.destinationFacilityId || "",
      supplierRmaId: "",
    };

    dispatch(
      updateReturnHeader({
        data: payload,
        callback: () => {
          fetchReturnDetails(returnId);
        },
      })
    );
  };

  return (
    <Loader loading={isLoading}>
      <SEO titleTemplate={t("Return Details")} description={t("Return Details.")} />
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{t("Reset cart")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t("Creating a substitute order will reset the current cart. Continue?")}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            {t("Cancel")}
          </Button>
          <Button variant="danger" onClick={createSubstituteOrder}>
            {t("Confirm")}
          </Button>
        </Modal.Footer>
      </Modal>
      <LayoutOne headerTop="visible">
        <Breadcrumb
          pages={[
            { label: t("Home"), path: process.env.PUBLIC_URL + "/" },
            {
              label: t("Return Details"),
              path: process.env.PUBLIC_URL + pathname,
            },
          ]}
        />

        <div className="cart-main-area pt-20 pb-100">
          <Container fluid className="cart-order-container">
            {returnDetails ? (
              <>
                <div className="d-flex justify-content-end align-items-center btn-hover mb-2">
                  {canCreateSubstituteOrder && (
                    <button className="default-btn me-2" onClick={handleCreateSubstituteOrder}>
                      {t("Create Substitute Order")}
                    </button>
                  )}
                  {/* {returnDetails?.statusId !== 'SUP_RETURN_ACCEPTED' && (
                    <button
                      className="default-btn me-2"
                      onClick={handleStatusChange}
                    >
                      Accept
                    </button>
                  )} */}
                </div>
                {/* SUMMARY CARD */}
                <Card className="mb-4">
                  <Card.Header>
                    <h5 className="mb-0">{t("Return Summary")}</h5>
                  </Card.Header>
                  <Card.Body>
                    <Row>
                      <Col md={6}>
                        <DetailRow
                          label={t("Return ID")}
                          value={returnDetails.returnId}
                        />
                        <DetailRow
                          label={t("Return Status")}
                          value={returnDetails.returnStatus}
                        />
                        <DetailRow
                          label={t("Return Type")}
                          value={returnDetails.returnHeaderTypeDesc}
                        />
                        {isSubstitutionReturn && (
                          <DetailRow
                            label={t("Minimum Substitute Order Value")}
                            value={formatReturnAmount(returnDetails?.orderValue)}
                          />
                        )}
                        <DetailRow
                          label={t("Original Order ID")}
                          value={
                            <Link to={`/orders/${orderId}`}>{orderId}</Link>
                          }
                        />
                        <DetailRow
                          label={t("Created By")}
                          value={returnDetails.createdBy || "—"}
                        />
                      </Col>
                      <Col md={6}>
                        <DetailRow
                          label={t("From Party")}
                          value={`${returnDetails.fromPartyName} (${returnDetails.fromPartyId})`}
                        />
                        <DetailRow
                          label={t("To Party")}
                          value={`${returnDetails.toPartyName} (${returnDetails.toPartyId})`}
                        />
                        <DetailRow
                          label={t("Facility")}
                          value={`${returnDetails.destinationFacilityIdDesc} (${returnDetails.destinationFacilityId})`}
                        />
                        <DetailRow
                          label={t("Currency")}
                          value={returnDetails.currencyUomId}
                        />
                        <DetailRow
                          label={t("Needs Inventory Receive")}
                          value={
                            returnDetails.needsInventoryReceive === "N"
                              ? t("No")
                              : t("Yes")
                          }
                        />

                        <DetailRow
                          label={t("Entry Date")}
                          value={formatDate(returnDetails?.entryDate)}
                        />
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>

                {/* RETURN ITEMS ACCORDION */}
                <Accordion defaultActiveKey="1" className="mt-3">
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>{t("Returned Items")}</Accordion.Header>
                    <Accordion.Body>
                      <Table
                        data={returnDetails.returnItemDetails || []}
                        columns={returnItemsColumns}
                        enableGlobalFilter={false}
                        enableSorting={false}
                        enablePagination={false}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>

                {/* Substitute Orders  */}
                <Accordion defaultActiveKey="1" className="mt-3">
                  <Accordion.Item eventKey="1">
                    <Accordion.Header>{t("Substitute Orders")}</Accordion.Header>
                    <Accordion.Body>
                      <Table
                        data={returnDetails?.substituteOrders || []}
                        columns={substituteOrdersColumns}
                        enableGlobalFilter={false}
                        enableSorting={false}
                        enablePagination={false}
                      />
                    </Accordion.Body>
                  </Accordion.Item>
                </Accordion>
              </>
            ) : (
              <p>{t("No return details available.")}</p>
            )}
          </Container>
        </div>
      </LayoutOne>
    </Loader>
  );
};

export default ReturnDetails;

/**
 * A small helper component for summary labels/values
 */
const DetailRow = ({ label, value }) => {
  return (
    <p className="mb-1">
      <strong style={{ fontWeight: 600 }}>{label}:</strong> {value}
    </p>
  );
};
